import React, { useState } from 'react'

import { getChainCurrencySymbolWithChainId, NETWORK_LIST } from '../../utils/config'
import UserListTable from '../Staking/components/UserListTable'
import NftCard from './nftCard'
import MintLimitPanel from './MintLimitPanel'
import NftDetailDialog from './NftDetailDialog'
import PaymentConfigPanel from './PaymentConfigPanel'
import { getNetworkDisplayName, isTestNetwork } from './networkPresentation'
import { NFT_TEMPLATE_V2, shouldShowPublicMintControls } from './templateAccess'

function ActionRow({ icon, title, description, onClick, danger = false }) {
    return <button type="button" className={`owner-action ${danger ? 'is-danger' : ''}`} onClick={onClick}>
        <span className="owner-action__icon"><i className={`fa ${icon}`} /></span>
        <span className="owner-action__copy">
            <strong>{title}</strong>
            <small>{description}</small>
        </span>
        <i className="fa fa-chevron-right owner-action__chevron" />
    </button>
}

function ReadinessItem({ ready, icon, label, value, valueTitle, showStateIcon = true }) {
    return <li className={ready ? 'is-ready' : 'is-pending'}>
        <i className={`fa ${ready && showStateIcon ? 'fa-check-circle' : icon}`} />
        <span>{label}</span>
        <small title={valueTitle}>{value}</small>
    </li>
}

export default function ConsoleView({ controller }) {
    const [descriptionExpanded, setDescriptionExpanded] = useState(false)
    const [selectedNft, setSelectedNft] = useState(null)
    const { state } = controller
    const { t, chainId: walletChainId } = controller.props
    const nft = state.nftInfo || {}
    const isPublicMint = nft.isOnlyAdmin === false
    const showPublicAdminControls = shouldShowPublicMintControls({ isOnlyAdmin: nft.isOnlyAdmin })
    const payment = controller.getPaymentDetails()
    const maxMintQuantity = controller.getMaxMintQuantity()
    const network = NETWORK_LIST.find((item) => Number(item.chainId) === Number(state.chainId))
    const networkName = getNetworkDisplayName(network, state.chainId, t)
    const testNetwork = isTestNetwork(network, state.chainId)
    const networkMatches = Boolean(state.account && Number(walletChainId) === Number(state.chainId))
    const rpcLatencyValue = !networkMatches
        ? t('需要切换网络')
        : state.rpcLatencyStatus === 'failed'
            ? t('延迟检测失败')
            : Number.isFinite(state.rpcLatencyMs)
                ? `${state.rpcLatencyMs} ms`
                : t('检测中')
    const rpcReady = networkMatches && state.rpcLatencyStatus !== 'failed'
    const soldOut = maxMintQuantity === 0
    const collectionName = nft.name || nft.symbol || 'NFT Collection'
    const description = state.ipfsInfo?.description || ''
    const descriptionNeedsCollapse = description.length > 160
    const customFields = (state.ipfsInfo?.extra || []).filter((item) => (
        String(item?.name || '').trim() || String(item?.content || '').trim()
    ))
    const linkedInviter = state.params?.invitor || ''
    const validLinkedInviter = /^0x[a-fA-F0-9]{40}$/.test(linkedInviter)
        && (!state.account || linkedInviter.toLowerCase() !== state.account.toLowerCase())
        && nft.enableInvitor
    const scrollToSection = (target) => {
        document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    if (state.loadingNft) {
        return <main className="console-main console-state-screen">
            <div className="console-loader" aria-live="polite">
                <span className="console-loader__signal"><i className="fa fa-cube" /></span>
                <strong>{t('正在加载NFT信息')}</strong>
                <small>{t('正在同步链上配置与项目资料')}</small>
            </div>
        </main>
    }

    if (state.nftLoadError) {
        return <main className="console-main console-state-screen">
            <div className="console-error" role="alert">
                <i className="fa fa-exclamation-triangle" />
                <strong>{t('加载NFT信息失败')}</strong>
                <p>{state.nftLoadError}</p>
                <button type="button" onClick={() => controller.fetchData()}><i className="fa fa-redo" /> {t('重试')}</button>
            </div>
        </main>
    }

    return <main className="console-main">
        <section className="console-hero" aria-label={t('铸造控制台')}>
            <div className="collection-visual">
                <div className="collection-visual__frame">
                    {state.ipfsInfo?.logo
                        ? <img src={state.ipfsInfo.logo} alt={collectionName} />
                        : <div className="collection-visual__fallback"><i className="fa fa-cube" /><span>{nft.symbol || 'NFT'}</span></div>}
                    <span className="collection-visual__corner top-left" />
                    <span className="collection-visual__corner top-right" />
                    <span className="collection-visual__corner bottom-left" />
                    <span className="collection-visual__corner bottom-right" />
                </div>
            </div>

            <div className="collection-summary">
                <span className="console-overline">{t('NFT系列')}</span>
                <h1>{collectionName}</h1>
                {description && <div className="collection-description">
                    <p data-testid="collection-description" className={!descriptionExpanded ? 'is-collapsed' : ''}>{description}</p>
                    {descriptionNeedsCollapse && <button type="button" onClick={() => setDescriptionExpanded((expanded) => !expanded)}>
                        {descriptionExpanded ? t('收起') : t('展开')} <i className={`fa fa-chevron-${descriptionExpanded ? 'up' : 'down'}`} />
                    </button>}
                </div>}

                <button type="button" className="contract-copy" onClick={() => controller.copyValue(state.contract)} title={t('复制完整合约地址')}>
                    <span>{t('合约')}</span>
                    <code>{controller.getShortAddress(state.contract)}</code>
                    <i className="fa fa-copy" />
                </button>

                <div className="supply-metrics">
                    <div><span>{t('总量')}</span><strong>{nft.maxSupply?.toString() || '0'}</strong></div>
                    <div><span>{t('已铸造')}</span><strong>{nft.totalSupply?.toString() || '0'}</strong></div>
                    <div><span>{t('您已铸造')}</span><strong>{state.account ? (nft.userMintCount?.toString() || '0') : '-'}</strong></div>
                </div>
                <div className="supply-progress" aria-label={`${controller.getProgressBarWidth()}%`}>
                    <span style={{ width: `${controller.getProgressBarWidth()}%` }} />
                </div>
                <div className="collection-tags">
                    <span className={isPublicMint ? 'is-live' : 'is-private'}>{isPublicMint ? t('公开铸造') : t('仅项目方铸造')}</span>
                    <span>ERC-721</span>
                </div>
            </div>

            <section className="mint-console">
                <div className="mint-console__heading">
                    <span className="console-overline">{t('铸造控制台')}</span>
                    <small>{networkName}</small>
                </div>

                {testNetwork && <div className="testnet-notice"><i className="fa fa-flask" /><span><strong>{t('测试网络')}</strong>{t('此网络资产没有实际价值')}</span></div>}

                {isPublicMint ? <>
                    <div className="mint-pricing">
                        <div>
                            <span>{t('单枚价格')}</span>
                            <strong>{payment.unit} <small>{payment.symbol}</small></strong>
                        </div>
                        <div>
                            <span>{t('支付资产')}</span>
                            <strong>{payment.symbol}</strong>
                        </div>
                    </div>

                    <div className="mint-quantity-row">
                        <span>{t('本次铸造数量')}</span>
                        <div className="quantity-stepper">
                            <button type="button" onClick={() => controller.changeMintQuantity(-1)} disabled={state.mintQuantity <= 1} aria-label={t('减少数量')}><i className="fa fa-minus" /></button>
                            <strong>{state.mintQuantity}</strong>
                            <button type="button" onClick={() => controller.changeMintQuantity(1)} disabled={soldOut || state.mintQuantity >= maxMintQuantity} aria-label={t('增加数量')}><i className="fa fa-plus" /></button>
                        </div>
                        <small>{nft.userMintLimit?.toString() !== '0' ? `${t('剩余可铸造')} ${maxMintQuantity}` : t('未设置单钱包上限')}</small>
                    </div>

                    <div className="mint-total">
                        <span>{t('合计')}</span>
                        <strong>{payment.total} {payment.symbol}</strong>
                    </div>

                    <ul className="mint-readiness">
                        <ReadinessItem ready={Boolean(state.account)} icon="fa-wallet" label={t('钱包')} value={state.account ? controller.getShortAddress(state.account) : t('未连接')} />
                        <ReadinessItem ready={rpcReady} icon="fa-network-wired" label={t('网络')} value={rpcLatencyValue} />
                        {validLinkedInviter && <ReadinessItem ready icon="fa-user-friends" label={t('邀请人')} value={controller.getShortAddress(linkedInviter)} valueTitle={linkedInviter} showStateIcon={false} />}
                    </ul>

                    <button type="button" className="mint-primary" onClick={() => controller.handleMintClick()} disabled={state.mintLoading || soldOut}>
                        {state.mintLoading ? <i className="fa fa-spinner loading-css" /> : <i className="fa fa-rocket" />}
                        <span>{soldOut ? t('已售罄') : state.mintLoading ? t('铸造中...') : t('铸造NFT')}</span>
                    </button>
                </> : <div className="admin-mint-notice">
                    <i className="fa fa-lock" />
                    <strong>{t('此系列不开放公开铸造')}</strong>
                    <p>{t('该系列由项目方在创建合约时完成初始铸造')}</p>
                </div>}
            </section>
        </section>

        {nft.enableInvitor && <section className="referral-share-panel" data-testid="referral-share-panel">
            <header className="referral-share-panel__heading">
                <i className="fa fa-share-alt" />
                <span className="console-overline">{t('邀请推广已开启')}</span>
            </header>
            <div className={`referral-share-panel__grid ${nft.invitor ? 'has-inviter' : ''}`}>
                <div className="referral-share-panel__item is-link">
                    <span className="referral-share-panel__icon"><i className="fa fa-link" /></span>
                    <div className="referral-share-panel__copy">
                        <strong>{t('我的邀请链接')}</strong>
                        {state.account
                            ? <code title={controller.generateShareLink()}>{controller.generateShareLink()}</code>
                            : <small>{t('连接钱包后即可生成专属邀请链接')}</small>}
                    </div>
                    <button type="button" className="referral-share-panel__action" disabled={!state.account} onClick={() => controller.copyValue(controller.generateShareLink())} aria-label={t('复制邀请链接')} title={t('复制邀请链接')}>
                        <i className="fa fa-copy" />
                    </button>
                </div>
                {nft.invitor && <button type="button" className="referral-share-panel__item is-inviter" onClick={() => controller.copyValue(nft.invitor)} aria-label={t('复制邀请人地址')} title={t('复制邀请人地址')}>
                    <span className="referral-share-panel__icon"><i className="fa fa-user-friends" /></span>
                    <span className="referral-share-panel__copy">
                        <strong>{t('我的邀请人')}</strong>
                        <code title={nft.invitor}>{controller.getShortAddress(nft.invitor)}</code>
                    </span>
                    <i className="fa fa-copy referral-share-panel__copy-icon" />
                </button>}
            </div>
        </section>}

        {customFields.length > 0 && <section className="project-info-cards" data-testid="project-custom-fields">
                {customFields.map((item, index) => {
                    const fieldName = String(item.name || '').trim()
                    const fieldContent = String(item.content || '').trim()
                    return <article className={`project-info-card ${fieldName ? '' : 'is-content-only'}`} key={`project-field-${index}`}>
                        {fieldName && <header className="project-info-card__heading"><h2>{fieldName}</h2></header>}
                        {fieldContent && <div className="project-info-card__body"><p>{fieldContent}</p></div>}
                    </article>
                })}
        </section>}

        <div className="workspace-labels">
            <button type="button" onClick={() => scrollToSection('wallet-assets')}><i className="fa fa-images" /> {t('我的NFT')}</button>
            {state.isAdmin && <button type="button" onClick={() => scrollToSection('owner-console')}><i className="fa fa-shield-alt" /> {t('管理员控制')}</button>}
        </div>

        <section className={`console-workspace ${state.isAdmin ? 'has-owner' : ''}`}>
            <section id="wallet-assets" className="wallet-assets">
                <header className="section-heading">
                    <div>
                        <span className="console-overline">{t('钱包资产')}</span>
                        <h2>{t('我的NFT')}</h2>
                    </div>
                    <span className="section-count">{state.account ? state.myNftCount : 0}</span>
                </header>

                {!state.account && <div className="section-empty">
                    <i className="fa fa-wallet" />
                    <strong>{t('连接钱包后查看您的NFT')}</strong>
                    <small>{t('页面会自动读取当前钱包持有的本系列NFT')}</small>
                </div>}

                {state.account && state.myNftList.length === 0 && <div className="section-empty">
                    <i className="fa fa-image" />
                    <strong>{t('当前钱包暂无本系列NFT')}</strong>
                    <small>{isPublicMint ? t('完成铸造后会显示在这里') : t('请切换持有NFT的钱包')}</small>
                </div>}

                {state.myNftList.length > 0 && <>
                    <div className="nft-grid">
                        {state.myNftList.map((item) => <NftCard key={`${state.contract}-${item.id}`} info={item} onOpen={setSelectedNft} />)}
                    </div>
                    {state.pagePaginate?.pageCount > 1 && <nav className="asset-pagination" aria-label={t('NFT分页')}>
                        <button type="button" onClick={() => controller.getMyNftList(state.pagePaginate.currentPage - 1)} disabled={state.pagePaginate.currentPage <= 1} aria-label={t('上一页')}><i className="fa fa-chevron-left" /></button>
                        {state.pagePaginate.list.map((item, index) => item.ellipsis
                            ? <span key={`ellipsis-${index}`}>...</span>
                            : <button type="button" key={`page-${item.page}`} className={state.pagePaginate.currentPage === item.page ? 'is-active' : ''} onClick={() => controller.getMyNftList(item.page)}>{item.page}</button>)}
                        <button type="button" onClick={() => controller.getMyNftList(state.pagePaginate.currentPage + 1)} disabled={state.pagePaginate.currentPage >= state.pagePaginate.pageCount} aria-label={t('下一页')}><i className="fa fa-chevron-right" /></button>
                    </nav>}
                </>}
            </section>

            {state.isAdmin && <section id="owner-console" className="owner-console">
                <header className="section-heading">
                    <div>
                        <span className="console-overline">{t('合约管理')}</span>
                        <h2>{t('管理员控制')}</h2>
                    </div>
                    <span className="owner-badge"><i className="fa fa-shield-alt" /> {t('当前管理员')}</span>
                </header>

                {state.templateId === NFT_TEMPLATE_V2 && nft.configVersion > 0 && <PaymentConfigPanel
                    beforeSubmit={() => controller.checkNetwork()}
                    contractAddress={state.contract}
                    currentConfig={{
                        mode: nft.feeMode,
                        tokenAddress: nft.feeCa,
                        priceRaw: nft.feePerNFT,
                        version: nft.configVersion,
                        createdBlock: nft.configCreatedBlock,
                        token: nft.feeToken,
                    }}
                    getProvider={() => controller.initProvider()}
                    nativeSymbol={getChainCurrencySymbolWithChainId(state.chainId)}
                    onCopy={(value) => controller.copyValue(value)}
                    onError={(error) => controller.handleErr(error)}
                    onUpdated={() => controller.fetchData()}
                    showMessage={(text) => controller.showMessageModal({ show: true, title: t('成功'), text, icon: 'success' })}
                    t={t}
                />}

                {state.templateId === NFT_TEMPLATE_V2 && nft.supportsMutableUserMintLimit && <MintLimitPanel
                    beforeSubmit={() => controller.checkNetwork()}
                    contractAddress={state.contract}
                    currentLimit={nft.userMintLimit}
                    getProvider={() => controller.initProvider()}
                    maxSupply={nft.maxSupply}
                    onError={(error) => controller.handleErr(error)}
                    onUpdated={() => controller.fetchData()}
                    showMessage={(text) => controller.showMessageModal({ show: true, title: t('成功'), text, icon: 'success' })}
                    t={t}
                />}

                <div className="owner-action-group">
                    <span className="owner-action-group__label">{t('项目设置')}</span>
                    {nft.isOnlyAdmin === true && <ActionRow icon="fa-layer-group" title={t('增发 NFT')} description={t('前往 FatSale 为当前系列分批增发 NFT')} onClick={() => controller.openMainSiteMint()} />}
                    <ActionRow icon="fa-store" title={t('创建交易市场')} description={t('为当前 NFT 系列创建专属交易与流动性入口')} onClick={() => controller.openMarketplaceGuide()} />
                    <ActionRow icon="fa-edit" title={t('设置项目信息')} description={t('修改项目Logo、简介与外部链接')} onClick={() => controller.openProjectInfo()} />
                    {(window.location.href.includes('fatsale') || window.location.href.includes('localhost')) && <ActionRow icon="fa-globe" title={t('绑定独立域名')} description={t('咨询独立域名与部署服务')} onClick={() => controller.openDomainInfo()} />}
                </div>

                {showPublicAdminControls && <div className="owner-action-group">
                    <span className="owner-action-group__label">{t('访问与推广')}</span>
                    <ActionRow icon="fa-users" title={nft.enableInvitor ? t('设置推广返利等级') : t('开启推广邀请')} description={t('管理邀请层级与返利比例')} onClick={() => {
                        if (controller.checkNetwork() === false) return
                        controller.setState({ showInviteConfirmModal: true })
                    }} />
                    {nft.enableInvitor && <ActionRow icon="fa-list-alt" title={t('设置白名单')} description={t('管理无需先铸造即可邀请的钱包')} onClick={() => {
                        if (controller.checkNetwork() === false) return
                        controller.setState({ showWhitelistModal: true })
                    }} />}
                    <ActionRow icon="fa-link" title={t('复制铸造页面地址')} description={t('复制当前公开铸造链接')} onClick={() => controller.copyValue(window.location.href)} />
                </div>}

                <div className="owner-action-group is-danger-zone">
                    <span className="owner-action-group__label">{t('所有权')}</span>
                    <ActionRow icon="fa-user-shield" title={t('转移所有权')} description={t('将合约管理权限转移到新地址')} onClick={() => controller.openOwnershipTransfer(false)} danger />
                    <ActionRow icon="fa-exclamation-triangle" title={t('放弃所有权')} description={t('不可逆操作，请谨慎确认')} onClick={() => controller.openOwnershipTransfer(true)} danger />
                </div>
            </section>}
        </section>

        {state.ipfsInfo?.youtube && <section className="project-video-console">
            <header className="section-heading">
                <div><h2>{t('项目视频')}</h2></div>
            </header>
            <div className="project-video-console__body">
                {state.ipfsInfo?.youtube && <div className="project-video"><iframe src={state.ipfsInfo.youtube} title={t('项目视频')} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen /></div>}
            </div>
        </section>}

        {state.isAdmin && showPublicAdminControls && <section className="data-console">
            <header className="section-heading"><div><span className="console-overline">{t('链上用户')}</span><h2>{t('管理员数据面板')}</h2></div><span className="section-count">{state.adminInfo.userCount || 0}</span></header>
            <UserListTable
                key="total-list-table"
                listKey="total-list-table"
                total={state.adminInfo.userCount}
                perPage={state.perPage}
                list={state.stakeUserList}
                changePage={(page) => controller.getStakeListForAdmin(page)}
                copy={(address) => controller.copyValue(address)}
                showReward={state.templateId !== NFT_TEMPLATE_V2}
            />
        </section>}

        {state.invitorTotalCount > 0 && <section className="data-console">
            <header className="section-heading">
                <div><span className="console-overline">{t('推广数据')}</span><h2>{t('我的邀请列表')}</h2></div>
                <span className="section-count">{state.invitorTotalCount}</span>
            </header>
            <div className="referral-summary">
                <span>{t('已邀请用户数')} <strong>{state.invitorTotalCount}</strong></span>
                {nft.enableInvitor && <span>{t('已获得的推荐奖励')} <strong>{nft.reward} {state.templateId === NFT_TEMPLATE_V2 ? nft.rewardSymbol : payment.symbol}</strong></span>}
            </div>
            <UserListTable
                key="user-list-table"
                listKey="user-list-table"
                total={state.invitorTotalCount}
                perPage={state.perPage}
                list={state.invitorList}
                changePage={(page) => controller.getInvitorList(page)}
                copy={(address) => controller.copyValue(address)}
                showReward={state.templateId !== NFT_TEMPLATE_V2}
            />
        </section>}

        <footer className="console-statusbar">
            <span className={networkMatches ? 'is-online' : 'is-offline'}><i className="fa fa-circle" /> {networkMatches ? t('已连接') : t('未就绪')}</span>
            <span>{networkName}</span>
            <a className="console-powered-by" href="https://fatsale.org" target="_blank" rel="noreferrer"><span>© 2026 · </span><span>{t('Powered by')}</span> <strong>FatSale</strong></a>
            <span className="console-statusbar__spacer" />
            <code>{state.account ? controller.getShortAddress(state.account) : t('未连接钱包')}</code>
        </footer>

        <NftDetailDialog
            details={selectedNft}
            contractAddress={state.contract}
            onClose={() => setSelectedNft(null)}
            onCopy={(value) => controller.copyValue(value)}
            onTransfer={(tokenId, recipient) => controller.transferNft(tokenId, recipient)}
            onTransferSuccess={() => controller.showMessageModal({ show: true, title: t('成功'), text: t('NFT 转账成功'), icon: 'success' })}
            t={t}
        />
    </main>
}
